import { Controller } from '@hotwired/stimulus'

/**
 * Hover Card Controller
 *
 * Shows a card/popover on hover over the trigger element.
 * Positions the card relative to the trigger based on placement value.
 * Named hover-card to avoid conflict with Flowbite's popover component.
 */
export default class extends Controller {
  static targets = ['trigger', 'content']

  static values = {
    placement: { type: String, default: 'right' }, // right, left, top, bottom
    delay: { type: Number, default: 200 }
  }

  connect() {
    this.showTimeout = null
    this.hideTimeout = null

    if (!this.hasTriggerTarget || !this.hasContentTarget) {
      return
    }

    // Bind event handlers
    this.triggerTarget.addEventListener('mouseenter', this.scheduleShow.bind(this))
    this.triggerTarget.addEventListener('mouseleave', this.scheduleHide.bind(this))
    this.contentTarget.addEventListener('mouseenter', this.cancelHide.bind(this))
    this.contentTarget.addEventListener('mouseleave', this.scheduleHide.bind(this))
  }

  disconnect() {
    this.hide()
    clearTimeout(this.showTimeout)
    clearTimeout(this.hideTimeout)
  }

  scheduleShow() {
    clearTimeout(this.hideTimeout)
    this.showTimeout = setTimeout(() => this.show(), this.delayValue)
  }

  scheduleHide() {
    clearTimeout(this.showTimeout)
    this.hideTimeout = setTimeout(() => this.hide(), 100)
  }

  cancelHide() {
    clearTimeout(this.hideTimeout)
  }

  show() {
    this.contentTarget.classList.remove('hidden')
    this.position()
  }

  hide() {
    this.contentTarget.classList.add('hidden')
  }

  position() {
    const trigger = this.triggerTarget
    const content = this.contentTarget
    const triggerRect = trigger.getBoundingClientRect()
    const contentRect = content.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Reset any previous positioning
    content.style.top = ''
    content.style.left = ''
    content.style.right = ''
    content.style.bottom = ''

    let placement = this.placementValue

    // Calculate available space
    const spaceRight = viewportWidth - triggerRect.right
    const spaceLeft = triggerRect.left
    const _spaceTop = triggerRect.top
    const _spaceBottom = viewportHeight - triggerRect.bottom

    // Auto-adjust placement if not enough space
    if (placement === 'right' && spaceRight < contentRect.width + 20) {
      placement = spaceLeft > spaceRight ? 'left' : 'bottom'
    } else if (placement === 'left' && spaceLeft < contentRect.width + 20) {
      placement = spaceRight > spaceLeft ? 'right' : 'bottom'
    }

    const gap = 8 // Gap between trigger and popover

    switch (placement) {
      case 'right':
        content.style.left = '100%'
        content.style.top = '0'
        content.style.marginLeft = `${gap}px`
        break
      case 'left':
        content.style.right = '100%'
        content.style.top = '0'
        content.style.marginRight = `${gap}px`
        break
      case 'top':
        content.style.bottom = '100%'
        content.style.left = '50%'
        content.style.transform = 'translateX(-50%)'
        content.style.marginBottom = `${gap}px`
        break
      case 'bottom':
        content.style.top = '100%'
        content.style.left = '50%'
        content.style.transform = 'translateX(-50%)'
        content.style.marginTop = `${gap}px`
        break
    }

    // Ensure popover stays within viewport
    requestAnimationFrame(() => {
      const updatedRect = content.getBoundingClientRect()

      // Adjust if overflowing right
      if (updatedRect.right > viewportWidth - 10) {
        const overflow = updatedRect.right - viewportWidth + 10
        content.style.left = `calc(100% - ${overflow}px)`
      }

      // Adjust if overflowing bottom
      if (updatedRect.bottom > viewportHeight - 10) {
        content.style.top = 'auto'
        content.style.bottom = '0'
      }
    })
  }
}
